import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    IconButton,
    Stack,
    List,
    ListItem,
    ListItemButton,
    TextField,
    Fab,
    alpha
} from "@mui/material";

import {
    GoogleMap,
    Circle,
    useJsApiLoader
} from "@react-google-maps/api";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MyLocationIcon from "@mui/icons-material/MyLocation";

const mapContainerStyle = { width: "100%", height: "100%" };
const defaultCenter = { lat: -33.4489, lng: -70.6693 };

const ModalConfiguracionGeocerca = ({ geocercas = [], onSave }) => {
    const [open, setOpen] = useState(false);
    const [zonas, setZonas] = useState([]);
    const [zonaActivaId, setZonaActivaId] = useState(null);
    const [editandoId, setEditandoId] = useState(null);

    const mapRef = useRef(null);
    const circlesRef = useRef({});

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
    });

    // ==========================================
    // CARGA INICIAL (CORREGIDA)
    // ==========================================
    useEffect(() => {
        if (open) {
            const normalizadas = geocercas.map((g, index) => ({
                id: g.id || `geo_stable_${index}`,
                nombre: g.nombre || `Zona ${index + 1}`,
                latitud: parseFloat(g.latitud) || defaultCenter.lat,
                longitud: parseFloat(g.longitud) || defaultCenter.lng,
                radio: parseFloat(g.radio) || 500
            }));
            setZonas(normalizadas);
            setZonaActivaId(normalizadas.length > 0 ? normalizadas[0].id : null);
            circlesRef.current = {};
        }
        // IMPORTANTE: 'geocercas' fue eliminado de las dependencias.
        // Así evitamos que un re-render del padre resetee el mapa y deje fantasmas.
    }, [open]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setEditandoId(null);
    };

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        if (zonas.length > 0) {
            map.panTo({ lat: zonas[0].latitud, lng: zonas[0].longitud });
        }
    }, [zonas]);

    // ==========================================
    // SINCRONIZACIÓN DE COORDENADAS
    // Mantiene a React enterado de dónde soltaste el círculo
    // ==========================================
    const handleCircleChange = useCallback((id) => {
        const circle = circlesRef.current[id];
        if (!circle) return;

        const newCenter = circle.getCenter();
        const newRadius = circle.getRadius();

        setZonas(prev => prev.map(z => 
            z.id === id 
                ? { ...z, latitud: newCenter.lat(), longitud: newCenter.lng(), radio: newRadius } 
                : z
        ));
    }, []);

    const centrarZona = (zona) => {
        setZonaActivaId(zona.id);
        if (mapRef.current) {
            mapRef.current.panTo({ lat: zona.latitud, lng: zona.longitud });
            mapRef.current.setZoom(15);
        }
    };

    const handleNombreChange = (id, nuevoNombre) => {
        setZonas(prev => prev.map(z => z.id === id ? { ...z, nombre: nuevoNombre } : z));
    };

    const procesarGuardado = () => {
        onSave(zonas);
        handleClose();
    };

    if (!isLoaded) return null;

    return (
        <>
            <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={handleOpen}
                startIcon={<LocationOnIcon />}
                sx={{
                    textTransform: "none", borderRadius: 2, fontWeight: 600,
                    bgcolor: alpha("#9C27B0", 0.1), color: "#9C27B0",
                    "&:hover": { bgcolor: alpha("#9C27B0", 0.2) }
                }}
            >
                Diseñar Zonas ({geocercas.length})
            </Button>

            <Modal open={open} onClose={handleClose}>
                <Box
                    sx={{
                        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                        width: "96vw", maxWidth: 1300, height: "90vh", bgcolor: "#fff",
                        borderRadius: 4, boxShadow: "0 24px 48px rgba(0,0,0,0.2)", display: "flex", overflow: "hidden"
                    }}
                >
                    <IconButton 
                        onClick={handleClose} 
                        sx={{ position: "absolute", top: 16, right: 16, zIndex: 10, bgcolor: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", "&:hover": { bgcolor: "#f5f5f5" } }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box sx={{ flex: 1, position: "relative" }}>
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={defaultCenter}
                            zoom={12}
                            onLoad={onMapLoad}
                            options={{
                                streetViewControl: false, mapTypeControl: true, fullscreenControl: false,
                                mapTypeControlOptions: { position: window.google?.maps?.ControlPosition?.TOP_RIGHT }
                            }}
                        >
                            {zonas.map((g) => {
                                const isSelected = zonaActivaId === g.id;
                                return (
                                    <Circle
                                        key={g.id}
                                        center={{ lat: g.latitud, lng: g.longitud }}
                                        radius={g.radio}
                                        options={{
                                            fillColor: isSelected ? "#2b90fd" : "#9e9e9e",
                                            strokeColor: isSelected ? "#1976d2" : "#757575",
                                            fillOpacity: isSelected ? 0.35 : 0.15,
                                            strokeWeight: isSelected ? 2 : 1,
                                            editable: isSelected, 
                                            draggable: isSelected 
                                        }}
                                        onLoad={(circle) => { circlesRef.current[g.id] = circle; }}
                                        onUnmount={() => { delete circlesRef.current[g.id]; }}
                                        onClick={() => centrarZona(g)}
                                        // Estos eventos actualizan a React cuando terminas de arrastrar
                                        onDragEnd={() => handleCircleChange(g.id)}
                                        onRadiusChanged={() => handleCircleChange(g.id)}
                                    />
                                );
                            })}
                        </GoogleMap>

                        <Box 
                            sx={{
                                position: "absolute", top: 24, left: 24, width: 340, maxHeight: "calc(100% - 48px)",
                                bgcolor: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(16px)",
                                borderRadius: 4, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.2)",
                                display: "flex", flexDirection: "column", overflow: "hidden",
                                border: "1px solid rgba(255,255,255,0.4)"
                            }}
                        >
                            <Box sx={{ p: 2.5, borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                                <Typography variant="h6" fontWeight={700} color="#1e293b" gutterBottom>
                                    Geocercas
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Selecciona una zona en la lista para editar su radio y posición en el mapa.
                                </Typography>
                            </Box>

                            <List sx={{ flex: 1, overflowY: "auto", p: 1 }}>
                                {zonas.length === 0 ? (
                                    <Box p={3} textAlign="center">
                                        <Typography variant="body2" color="text.disabled">No hay zonas configuradas para esta alerta.</Typography>
                                    </Box>
                                ) : (
                                    zonas.map((z) => {
                                        const isActiva = zonaActivaId === z.id;
                                        return (
                                            <ListItem key={z.id} disablePadding sx={{ mb: 1 }}>
                                                <ListItemButton 
                                                    onClick={() => centrarZona(z)}
                                                    sx={{ borderRadius: 2, bgcolor: isActiva ? alpha("#2b90fd", 0.1) : "transparent", transition: "all 0.2s" }}
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                        {editandoId === z.id ? (
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <TextField 
                                                                    size="small" autoFocus variant="standard"
                                                                    value={z.nombre}
                                                                    onChange={(e) => handleNombreChange(z.id, e.target.value)}
                                                                    onBlur={() => setEditandoId(null)}
                                                                    onKeyDown={(e) => e.key === 'Enter' && setEditandoId(null)}
                                                                    fullWidth
                                                                />
                                                                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setEditandoId(null); }}>
                                                                    <CheckCircleIcon />
                                                                </IconButton>
                                                            </Box>
                                                        ) : (
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                                    <LocationOnIcon sx={{ color: isActiva ? "#2b90fd" : "#94a3b8", fontSize: 20 }} />
                                                                    <Typography fontWeight={isActiva ? 600 : 500} color={isActiva ? "#0f172a" : "#475569"}>
                                                                        {z.nombre}
                                                                    </Typography>
                                                                </Stack>
                                                                
                                                                <Stack direction="row">
                                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditandoId(z.id); }}>
                                                                        <EditOutlinedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                                                                    </IconButton>
                                                                </Stack>
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })
                                )}
                            </List>

                            <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
                                <Button
                                    fullWidth variant="contained"
                                    onClick={procesarGuardado}
                                    disableElevation
                                    sx={{ borderRadius: 2, py: 1.2, fontWeight: 600, background: "linear-gradient(135deg, #2b90fd, #1976d2)", textTransform: "none" }}
                                >
                                    Guardar Cambios
                                </Button>
                            </Box>
                        </Box>

                        {zonas.length > 0 && (
                            <Fab 
                                size="medium" 
                                sx={{ position: "absolute", bottom: 24, right: 24, bgcolor: "#fff", color: "#64748b", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
                                onClick={() => mapRef.current?.panTo({ lat: zonas[0].latitud, lng: zonas[0].longitud })}
                            >
                                <MyLocationIcon />
                            </Fab>
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default ModalConfiguracionGeocerca;